import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Grid,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getProjectCategoriesAPI,
  getProjectSubCategoriesAPI
} from '../../lib/api';
import LoadingProgress from '../../components/common/LoadingProgress';

const BudgetCategories = ({ customerId, projectId, onBack }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loadingSub, setLoadingSub] = useState({});
  const [error, setError] = useState("");

  const params = useParams();
  const navigate = useNavigate();

  const effectiveCustomerId = customerId || params.id;
  const effectiveProjectId = projectId || params.projectId;

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0
    }).format(Number(amount || 0));

  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true);
        const res = await getProjectCategoriesAPI(
          effectiveCustomerId,
          effectiveProjectId
        );

        if (res?.status && res?.data?.data) {
          setCategories(res.data.data);
        } else {
          setError("Failed to load categories");
        }
      } catch (err) {
        setError("Unable to fetch categories");
      } finally {
        setLoading(false);
      }
    }

    if (effectiveCustomerId && effectiveProjectId) {
      fetchCategories();
    }
  }, [effectiveCustomerId, effectiveProjectId]);

  const handleExpand = async (categoryName) => { 
    const isSame = expandedCategory === categoryName;
    setExpandedCategory(isSame ? null : categoryName);

    if (subCategories[categoryName]) return; 

    try {
      setLoadingSub((prev) => ({ ...prev, [categoryName]: true }));
      const res = await getProjectSubCategoriesAPI(
        effectiveCustomerId,
        effectiveProjectId,
        categoryName
      );

      setSubCategories((prev) => ({
        ...prev,
        [categoryName]: res?.data?.data || []
      }));
    } catch (err) {
      setSubCategories((prev) => ({
        ...prev,
        [categoryName]: []
      }));
    } finally {
      setLoadingSub((prev) => ({ ...prev, [categoryName]: false }));
    }
  }; 

  const handleBack = () => {
    if (onBack) return onBack();
    navigate(`/customers/${effectiveCustomerId}/project/${effectiveProjectId}`);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#fff", minHeight: "100vh", width: "100%" }}>
      <LoadingProgress loading={loading} />

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          onClick={handleBack}
          sx={{ color: colors.primary.main, mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            flex: 1,
            fontSize: { xs: "1.3rem", sm: "1.6rem" }
          }}
        >
          Budget Categories
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && categories.length === 0 && (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <CategoryIcon sx={{ fontSize: 50, color: colors.text.disabled }} />
          <Typography>No Categories Found</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
            xl: "repeat(5, 1fr)"
          },
          gap: 2,
          width: "100%",
          gridAutoFlow: "dense",
          alignItems: "start"
        }}
      >
        {categories.map((cat, index) => (
          <Box
            key={index}
            sx={{
              position: "relative",
              minHeight: expandedCategory === cat.category ? "auto" : "80px"
            }}
          >
            <Accordion
              expanded={expandedCategory === cat.category}
              onChange={() => handleExpand(cat.category)}
              sx={{
                border: `1px solid ${colors.border.main}`,
                borderRadius: "12px !important",
                overflow: "visible",
                boxShadow: expandedCategory === cat.category ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
                width: "100%",
                "&:before": { display: "none" },
                transition: "all 0.3s ease",
                position: expandedCategory === cat.category ? "absolute" : "static",
                zIndex: expandedCategory === cat.category ? 10 : 1,
                top: 0,
                left: 0,
                right: 0
              }}
            >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: { xs: 56, sm: 64 },
              "&.Mui-expanded": {
                minHeight: { xs: 56, sm: 64 }
              },
              "& .MuiAccordionSummary-content": {
                my: { xs: 1.5, sm: 2 },
                alignItems: "center",
                gap: 1
              }
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                {cat.category}
              </Typography>
            </Box>

            <Chip
              label={formatAmount(cat.total_amount)}
              sx={{
                bgcolor: colors.success.light,
                color: colors.success.dark,
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.8125rem" }
              }}
            />
          </AccordionSummary>

          <AccordionDetails
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              width: "100%",
              boxSizing: "border-box",
              bgcolor: "#fafafa",
              borderTop: `1px solid ${colors.border.light}`
            }}
          >
            {loadingSub[cat.category] ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : subCategories[cat.category]?.length > 0 ? (

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: "100%"
                }}
              >
                {subCategories[cat.category].map((sub, i) => (
                  <Card
                    key={i}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${colors.border.main}`,
                      bgcolor: "#ffffff",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        borderColor: colors.primary.main
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: colors.text.primary
                      }}
                    >
                      {sub.sub_category}
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: colors.success.main
                      }}
                    >
                      {formatAmount(sub.total_amount)}
                    </Typography>
                  </Card>
                ))}
              </Box>

            ) : (
              <Typography
                sx={{
                  textAlign: "center",
                  color: colors.text.secondary,
                  py: 2
                }}
              >
                No Sub-Categories Available
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BudgetCategories;


